import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AppConstants } from '../commons/app-constants';

/** sessionStorage key set when someone ticks "I agree" on the sign-up page and starts a social sign-up (the page is left for the provider). */
export const SIGNUP_AGREED_KEY = 'llSignupAgreedAt';

export interface LegalVersionInfo {
  version: string;
  effectiveDate: string;
}

export interface LegalAcceptanceStatus {
  documentType: string;
  latestVersion: string;
  effectiveDate: string;
  yourVersion: string | null;
  acceptedAt: string | null;
  isLatest: boolean;
  /** Only ever populated for documentType==='COOKIE' — null for every other document type. */
  preferences: { analytics?: boolean } | null;
}

export interface OutdatedDoc {
  documentType: string;
  latestVersion: string;
  yourVersion: string | null;
}

export interface LegalDocumentContent {
  documentType: string;
  title: string;
  body: string;
  version: string;
  effectiveDate: string;
}

@Injectable({ providedIn: 'root' })
export class LegalService {

  private readonly base = `${AppConstants.API_URL}api/v1/legal`;

  /** Emits whenever an acceptance was recorded (account setup, sign-up, the dialog): the app shell then re-reads what is outstanding. */
  readonly acceptancesChanged$ = new Subject<void>();
  notifyAcceptancesChanged(): void { this.acceptancesChanged$.next(); }

  constructor(private http: HttpClient) {}

  getCurrentVersions(): Observable<Record<string, LegalVersionInfo>> {
    return this.http.get<Record<string, LegalVersionInfo>>(`${this.base}/current-versions`)
      .pipe(catchError(() => of({})));
  }

  /** The real, admin-published document content — same source `/legal/{type}` pages render.
   *  Used by LegalAcceptanceDialogComponent so every acceptance checkbox shows the actual
   *  current text instead of a separately-hardcoded copy. */
  getDocument(documentType: string): Observable<LegalDocumentContent> {
    return this.http.get<LegalDocumentContent>(`${this.base}/document/${documentType}`);
  }

  /** timezone: the browser's IANA zone at the moment of acceptance
   *  (Intl.DateTimeFormat().resolvedOptions().timeZone) — optional, added 2026-08-27 (prompt187).
   *  IP/location/device are captured server-side automatically, nothing to pass from here. */
  recordAcceptance(documentType: string, version: string, timezone?: string): Observable<void> {
    return this.http.post<void>(`${this.base}/accept`, { documentType, version, timezone });
  }

  recordBatchAcceptance(items: { documentType: string; version: string }[]): Observable<void> {
    return this.http.post<void>(`${this.base}/accept-batch`, items);
  }

  /** Records the current user's Cookie Consent choice (Analytics opt-in/out) against a
   *  specific policy version — a dedicated endpoint since the generic accept endpoints' plain
   *  string body can't cleanly carry a real boolean. */
  recordCookieAcceptance(version: string, analytics: boolean): Observable<void> {
    return this.http.post<void>(`${this.base}/accept-cookie`, { version, analytics });
  }

  getMyAcceptances(): Observable<LegalAcceptanceStatus[]> {
    return this.http.get<LegalAcceptanceStatus[]>(`${this.base}/my-acceptances`)
      .pipe(catchError(() => of([])));
  }

  getOutdated(): Observable<OutdatedDoc[]> {
    return this.http.get<OutdatedDoc[]>(`${this.base}/my-outdated`)
      .pipe(catchError(() => of([])));
  }
}
